import React, { useState, useMemo } from 'react';
import { Card, IconButton, Button } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import PropTypes from 'prop-types';
import ArgonTypography from 'components/ArgonTypography';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArgonInput from 'components/ArgonInput';
import ArgonSelect from 'components/ArgonSelect';
import ClearIcon from '@mui/icons-material/Clear';
import DeployProductTable from '../tabels/DeployProductTable';

const getProductGroupId = (product) => product.external_id || product.sf_id || product.name;

function DeployProduct({
  orgs,
  variants,
  selectedProducts,
  versionChoices,
  onVersionChange,
  onToggleSelection,
  onClearSelections,
  itemsPerPage = 15,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const orgsMap = useMemo(() => {
    if (!orgs || orgs.length === 0) return new Map();
    return new Map(orgs.map((org) => [org.org_id, org.name]));
  }, [orgs]);

  const groupedProducts = useMemo(() => {
    const filtered = (variants || []).filter(
      (variant) => !searchTerm || variant.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const groups = filtered.reduce((acc, product) => {
      // Group by name + org_id to ensure all versions from the same org are together
      const groupKey = `${product.name}|${product.org_id || ''}`;
      if (!acc[groupKey]) {
        acc[groupKey] = { groupId: groupKey, name: product.name, org_id: product.org_id, allVersions: [] };
      }
      acc[groupKey].allVersions.push(product);
      return acc;
    }, {});
    return Object.values(groups);
  }, [variants, searchTerm]);

  const compareVersions = (a, b) => {
    const aParts = a.split('.').map(Number);
    const bParts = b.split('.').map(Number);
    for (let i = 0; i < 3; i++) {
      if ((aParts[i] || 0) > (bParts[i] || 0)) return -1;
      if ((aParts[i] || 0) < (bParts[i] || 0)) return 1;
    }
    return 0;
  };

  const tableRows = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedGroups = groupedProducts.slice(startIndex, startIndex + itemsPerPage);

    return paginatedGroups.map((group) => {
      const { groupId, name, org_id, allVersions } = group;
      // Check if any product with the same name and org_id is selected
      const isSelected = selectedProducts.some((p) => p.name === name && (p.org_id || '') === (org_id || ''));
      const hasVersionChoice = !!versionChoices[groupId];
      const isSelectable = hasVersionChoice || isSelected;

      const sortedVersions = [...allVersions].sort((a, b) => compareVersions(a.product_version, b.product_version));
      const versionOptions = sortedVersions.map((p) => ({ value: p.product_version, label: p.product_version }));
      const dropdownValue = versionChoices[groupId] || '';
      
      console.log(`[DeployProduct] Product "${name}" has ${versionOptions.length} versions:`, versionOptions.map(v => v.value));

      const orgName = orgsMap.get(org_id) || org_id; // Fallback to ID if name not found

      return {
        id: groupId,
        product: (
          <ArgonTypography variant="caption" fontWeight="bold">
            {name}
          </ArgonTypography>
        ),
        source_org: (
          <ArgonTypography variant="caption" color="secondary">
            {orgName}
          </ArgonTypography>
        ),
        select_version: (
          <ArgonSelect
            placeholder="Select Version"
            value={dropdownValue}
            options={versionOptions}
            onChange={(e) => onVersionChange(group, e.target.value)}
            onClick={(event) => event.stopPropagation()}
          />
        ),
        onClick: (e) => {
          if (e.target.closest('.MuiInputBase-root, .MuiSelect-select')) return;
          onToggleSelection(group);
        },
        selected: isSelected,
        isselectable: isSelectable,
      };
    });
  }, [
    currentPage,
    itemsPerPage,
    groupedProducts,
    selectedProducts,
    versionChoices,
    onVersionChange,
    onToggleSelection,
  ]);

  const totalPages = Math.ceil(groupedProducts.length / itemsPerPage) || 1;

  return (
    <ArgonBox py={3}>
      <ArgonBox display="flex" alignItems="center" mb={2} gap={2}>
        <ArgonInput
          type="text"
          placeholder="Search by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flexGrow: 1 }}
        />
        <Button
          sx={{ width: '140px' }}
          variant="contained"
          color="info"
          onClick={onClearSelections}
          startIcon={<ClearIcon />}
        >
          Clear ({selectedProducts?.length || 0})
        </Button>
      </ArgonBox>
      <Card>
        <ArgonBox>
          <DeployProductTable
            rows={tableRows}
            columns={[
              { name: 'product', align: 'left' },
              { name: 'source_org', align: 'center' },
              { name: 'select_version', align: 'center' },
            ]}
          />
        </ArgonBox>
        {totalPages > 1 && (
          <ArgonBox display="flex" justifyContent="center" mt={2} mb={1} gap={1}>
            <IconButton onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
              <ArrowLeftIcon />
            </IconButton>
            <ArgonTypography variant="body2" color="text" sx={{ alignSelf: 'center' }}>
              Page {currentPage} of {totalPages}
            </ArgonTypography>
            <IconButton
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ArrowRightIcon />
            </IconButton>
          </ArgonBox>
        )}
      </Card>
    </ArgonBox>
  );
}

DeployProduct.propTypes = {
  orgs: PropTypes.array.isRequired,
  variants: PropTypes.array.isRequired,
  selectedProducts: PropTypes.array.isRequired,
  versionChoices: PropTypes.object.isRequired,
  onVersionChange: PropTypes.func.isRequired,
  onToggleSelection: PropTypes.func.isRequired,
  onClearSelections: PropTypes.func.isRequired,
  itemsPerPage: PropTypes.number,
};

export default DeployProduct;
